/** Normalize Content-ID / cid: src values for lookup (case-insensitive). */
export function normalizeContentId(raw) {
  if (raw == null || raw === '') return '';
  let s = String(raw).trim();
  if (/^cid:/i.test(s)) s = s.slice(4);
  s = s.replace(/^<|>$/g, '').trim();
  return s.toLowerCase();
}

/** Authenticated Gmail attachment URL — works in <img src> on same origin (session cookie). */
export function gmailAttachmentUrl(mailboxId, messageId, attachmentId, apiBase = '') {
  return `${apiBase}/api/gmail/mailboxes/${encodeURIComponent(mailboxId)}/messages/${encodeURIComponent(messageId)}/attachments/${encodeURIComponent(attachmentId)}`;
}

function attachmentContentId(att) {
  if (!att) return '';
  return normalizeContentId(
    att.content_id || att.contentId || att.contentID || att.cid || '',
  );
}

export function buildCidAttachmentMap(attachments) {
  const map = new Map();
  (Array.isArray(attachments) ? attachments : []).forEach((att) => {
    if (!att || !att.attachmentId) return;
    const cid = attachmentContentId(att);
    if (cid) map.set(cid, att);
  });
  return map;
}

function isImageAttachment(att) {
  const ct = (att && (att.content_type || att.mimeType)) || '';
  if (/^image\//i.test(ct)) return true;
  const fn = (att && att.filename) || '';
  return /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(fn);
}

function imageAttachmentsForInline(attachments) {
  return (Array.isArray(attachments) ? attachments : [])
    .filter((a) => a && a.attachmentId && isImageAttachment(a));
}

/** Resolve a cid: src to an attachment — Content-ID map, then ordered / sole-image heuristics. */
export function resolveInlineImageAttachment(cidRaw, attachments, { cidMap, cidIndex = 0, cidCount = 1 } = {}) {
  const map = cidMap || buildCidAttachmentMap(attachments);
  const key = normalizeContentId(cidRaw);
  if (key && map.has(key)) return map.get(key);

  const imageAtts = imageAttachmentsForInline(attachments);
  if (cidCount > 0 && imageAtts.length === cidCount && cidIndex < imageAtts.length) {
    return imageAtts[cidIndex];
  }
  if (cidCount === 1 && imageAtts.length === 1) return imageAtts[0];
  return null;
}

/** Max edge length (px) for decorative/tracker imgs with no alt — hide when load fails. */
export const DECORATIVE_IMAGE_MAX_PX = 48;

export function parseImgDimensionPx(value) {
  if (value == null || value === '') return null;
  const n = parseInt(String(value).trim(), 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** Email UX: decorative when empty alt and both declared dimensions ≤ threshold (or unset). */
export function isDecorativeEmailImgAttrs({ alt, width, height, naturalWidth, naturalHeight } = {}) {
  if (String(alt || '').trim()) return false;
  const w = parseImgDimensionPx(width);
  const h = parseImgDimensionPx(height);
  if (w != null || h != null) {
    const maxDeclared = Math.max(w || 0, h || 0);
    return maxDeclared > 0 && maxDeclared <= DECORATIVE_IMAGE_MAX_PX;
  }
  if (naturalWidth > 0 && naturalHeight > 0) {
    return naturalWidth <= DECORATIVE_IMAGE_MAX_PX && naturalHeight <= DECORATIVE_IMAGE_MAX_PX;
  }
  return false;
}

/** Fallback label when an inline image cannot load — prefer meaningful alt (email best practice). */
export function failedEmailImgFallbackLabel({ alt, title } = {}) {
  const altText = String(alt || '').trim();
  if (altText) return altText;
  const titleText = String(title || '').trim();
  if (titleText) return titleText;
  return 'Image unavailable';
}

/** After load error — hide decorative imgs; show alt or placeholder for content imgs. */
export function applyFailedEmailImgFallback(img) {
  if (!img || img.dataset.emailImgFailed) return;
  img.dataset.emailImgFailed = '1';

  const attrs = {
    alt: img.getAttribute('alt'),
    width: img.getAttribute('width'),
    height: img.getAttribute('height'),
    naturalWidth: img.naturalWidth,
    naturalHeight: img.naturalHeight,
  };

  if (isDecorativeEmailImgAttrs(attrs)) {
    img.style.display = 'none';
    return;
  }

  const span = document.createElement('span');
  span.className = 'email-body-img-unavailable';
  span.textContent = `[${failedEmailImgFallbackLabel({
    alt: attrs.alt,
    title: img.getAttribute('title'),
  })}]`;
  const w = parseImgDimensionPx(attrs.width);
  if (w) span.style.maxWidth = `${w}px`;
  img.replaceWith(span);
}

// cid: with or without angle brackets, e.g. cid:logo@x or cid:<logo@x>
const IMG_SRC_CID_RE = /(<img\b[^>]*?\bsrc\s*=\s*)(["']?)(cid:(?:[^"'>]+|<[^">]+>))\2/gi;
const IMG_HTTP_SRC_RE = /(<img\b[^>]*?\bsrc\s*=\s*["'])http:\/\//gi;

function countCidImages(html) {
  let n = 0;
  const re = new RegExp(IMG_SRC_CID_RE.source, 'gi');
  html.replace(re, () => { n += 1; return ''; });
  return n;
}

/** Pull &lt;style&gt; blocks from a full HTML document (marketing emails). */
export function extractEmailHtmlHeadStyles(html) {
  const raw = html != null ? String(html) : '';
  if (!raw.trim()) return '';
  const styles = [];
  const re = /<style[^>]*>([\s\S]*?)<\/style>/gi;
  let m;
  while ((m = re.exec(raw)) !== null) {
    styles.push(`<style>${m[1]}</style>`);
  }
  return styles.join('\n');
}

function isStylesheetLinkTag(tag) {
  return /\brel=(["'])([^"']*\bstylesheet\b[^"']*)\1/i.test(tag)
    || /\brel=stylesheet\b/i.test(tag);
}

/** Pull &lt;link rel="stylesheet"&gt; tags from email HTML documents. */
function extractEmailHtmlHeadLinks(html) {
  const raw = html != null ? String(html) : '';
  if (!raw.trim()) return '';
  const links = [];
  const re = /<link\b[^>]*>/gi;
  let m;
  while ((m = re.exec(raw)) !== null) {
    if (isStylesheetLinkTag(m[0])) links.push(m[0]);
  }
  return links.join('\n');
}

/** Preserve body tag presentation attrs when lifting body inner HTML into an iframe. */
export function parseEmailBodyOpenAttrs(html) {
  const m = String(html || '').match(/<body([^>]*)>/i);
  if (!m) return '';
  const attrs = m[1] || '';
  const out = [];
  const style = attrs.match(/\bstyle=(["'])([\s\S]*?)\1/i);
  let styleVal = style ? style[2].trim() : '';
  const bg = attrs.match(/\bbgcolor=(["']?)([^"'>\s]+)\1/i);
  if (bg) {
    const bgCss = `background-color:${bg[2]}`;
    styleVal = styleVal ? `${styleVal.replace(/;$/, '')};${bgCss}` : bgCss;
  }
  if (styleVal) out.push(` style="${styleVal.replace(/"/g, '&quot;')}"`);
  const cls = attrs.match(/\bclass=(["'])([\s\S]*?)\1/i);
  if (cls) out.push(` class="${cls[2].replace(/"/g, '&quot;')}"`);
  return out.join('');
}

/** Strip document wrapper — return embeddable body markup (handles Gmail full-document body_html). */
export function unwrapEmailHtmlDocument(html) {
  const raw = html != null ? String(html) : '';
  if (!raw.trim()) return '';
  if (!/<html[\s>]/i.test(raw) && !/<!DOCTYPE/i.test(raw)) return raw;

  const bodyOpen = raw.match(/<body[^>]*>/i);
  if (bodyOpen) {
    const start = bodyOpen.index + bodyOpen[0].length;
    const closeRe = /<\/body>/gi;
    let lastClose = -1;
    let m;
    while ((m = closeRe.exec(raw)) !== null) {
      lastClose = m.index;
    }
    if (lastClose > start) {
      return raw.slice(start, lastClose);
    }
  }

  return raw
    .replace(/<!DOCTYPE[^>]*>/gi, '')
    .replace(/<\/?html[^>]*>/gi, '')
    .replace(/<head[\s\S]*?<\/head>/gi, '')
    .replace(/<\/?body[^>]*>/gi, '');
}

const NOTE_IFRAME_BASE_STYLES = `
  html, body { margin: 0; padding: 0; width: 100%; overflow-x: auto; box-sizing: border-box; }
  *, *::before, *::after { box-sizing: inherit; }
  body { font-family: Roboto, Helvetica, Arial, sans-serif; font-size: 14px; line-height: 1.4; padding: 8px 12px 12px; }
  img { max-width: 100% !important; height: auto !important; }
  table { max-width: 100% !important; }
  .email-note-header { margin: 0 0 12px; padding: 0 8px; }
  .email-note-header hr { margin: 8px 0; }
  .email-body-plain { padding: 0; white-space: pre-wrap; word-break: break-word; }
  .email-body-img-unavailable {
    color: rgba(0, 0, 0, 0.54); display: inline-block; font-size: 0.75rem;
    font-style: italic; line-height: 1.4; word-break: break-word;
  }
`;

function noteContentLooksLikeHtml(raw) {
  return /<\/?[a-z][\s\S]*>/i.test(raw);
}

function escapeHtmlText(text) {
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function plainTextToIframeFragment(text) {
  const s = text != null ? String(text) : '';
  if (!s.trim()) return '';
  return `<div class="email-body-plain">${escapeHtmlText(s).replace(/(?:\r\n|\r|\n)/g, '<br>')}</div>`;
}

/** Single srcdoc document for note iframe — merges header fragment + unwrapped email HTML. */
export function buildNoteIframeSrcdoc(noteContent) {
  const raw = noteContent != null ? String(noteContent) : '';
  if (!raw.trim()) return '';

  const extraStyles = extractEmailHtmlHeadStyles(raw);
  const extraLinks = extractEmailHtmlHeadLinks(raw);
  let inner = raw;
  let bodyAttrs = '';
  const htmlIdx = raw.search(/<html[\s>]/i);
  if (htmlIdx >= 0) {
    const header = raw.slice(0, htmlIdx);
    const docPart = raw.slice(htmlIdx);
    bodyAttrs = parseEmailBodyOpenAttrs(docPart);
    inner = header + unwrapEmailHtmlDocument(docPart);
  } else if (/<!DOCTYPE/i.test(raw)) {
    bodyAttrs = parseEmailBodyOpenAttrs(raw);
    inner = unwrapEmailHtmlDocument(raw);
  } else if (!noteContentLooksLikeHtml(raw)) {
    inner = plainTextToIframeFragment(raw);
  }

  if (extraStyles) {
    inner = inner.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');
  }
  if (extraLinks) {
    inner = inner.replace(/<link\b[^>]*>/gi, (tag) => (isStylesheetLinkTag(tag) ? '' : tag));
  }

  return `<!DOCTYPE html><html><head><meta charset="utf-8"><base target="_blank">`
    + `<meta name="viewport" content="width=device-width, initial-scale=1">`
    + `<style>${NOTE_IFRAME_BASE_STYLES}</style>${extraStyles}${extraLinks}`
    + `</head><body${bodyAttrs}>${inner}</body></html>`;
}

/** Measure rendered document height inside an iframe. */
export function measureIframeContentHeight(iframe) {
  if (!iframe || !iframe.contentDocument) return 0;
  const doc = iframe.contentDocument;
  const body = doc.body;
  const html = doc.documentElement;
  const heights = [
    html ? html.scrollHeight : 0,
    html ? html.offsetHeight : 0,
    body ? body.scrollHeight : 0,
    body ? body.offsetHeight : 0,
  ];
  if (body && body.lastElementChild) {
    const last = body.lastElementChild.getBoundingClientRect();
    const bodyTop = body.getBoundingClientRect().top;
    heights.push(last.bottom - bodyTop + (body.scrollTop || 0));
  }
  return Math.max(...heights.filter((h) => Number.isFinite(h) && h > 0), 0);
}

/** Size iframe to its document height (cap optional). */
export function resizeIframeToContent(iframe, { maxHeight = 2400, minHeight = 320 } = {}) {
  if (!iframe || !iframe.contentDocument) return;
  const h = measureIframeContentHeight(iframe);
  const cap = Number.isFinite(maxHeight) && maxHeight > 0 ? maxHeight : Infinity;
  iframe.style.height = `${Math.min(Math.max(h + 8, minHeight), cap)}px`;
  iframe.style.overflowY = h + 8 > cap ? 'auto' : 'hidden';
}

/** Re-measure after layout/images so long marketing emails are not clipped. */
export function bindIframeAutoResize(iframe, options = {}) {
  if (!iframe) return;
  const run = () => resizeIframeToContent(iframe, options);
  run();
  if (typeof requestAnimationFrame !== 'undefined') {
    requestAnimationFrame(run);
  }
  setTimeout(run, 100);
  setTimeout(run, 500);
  setTimeout(run, 1500);
  setTimeout(run, 3000);
  try {
    const doc = iframe.contentDocument;
    if (doc && doc.body) {
      doc.querySelectorAll('img').forEach((img) => {
        if (!img.complete) img.addEventListener('load', run, { once: true });
        img.addEventListener('error', run, { once: true });
      });
      if (typeof ResizeObserver !== 'undefined') {
        const ro = new ResizeObserver(run);
        ro.observe(doc.body);
        iframe._nuIframeResizeObserver = ro;
      }
    }
  } catch (e) { /* cross-origin guard */ }
}

/** Rewrite cid: inline images to API attachment URLs; harden external http/https imgs. */
export function prepareEmailBodyHtml(html, { mailboxId, messageId, attachments, apiBase = '' } = {}) {
  const raw = html != null ? String(html) : '';
  if (!raw.trim() || !mailboxId || !messageId) return raw;

  const cidMap = buildCidAttachmentMap(attachments);
  const cidCount = countCidImages(raw);
  let cidIndex = 0;

  let out = raw.replace(IMG_SRC_CID_RE, (match, prefix, quote, cidSrc) => {
    const att = resolveInlineImageAttachment(cidSrc, attachments, { cidMap, cidIndex, cidCount });
    cidIndex += 1;
    if (att && att.attachmentId) {
      const url = gmailAttachmentUrl(mailboxId, messageId, att.attachmentId, apiBase);
      return `${prefix}${quote}${url}${quote}`;
    }
    return match;
  });

  // Mixed content: try https for remote signature logos (some hosts redirect).
  out = out.replace(IMG_HTTP_SRC_RE, '$1https://');

  // Hotlink blocks — only when not already set.
  out = out.replace(/<img(?![^>]*\breferrerpolicy=)/gi, '<img referrerpolicy="no-referrer" ');

  return out;
}
